import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { RefreshCw } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"

import { isNoManagedTemplateError } from "./presentation-brand-spec-utils"
import {
  useAnalyzePresentationTemplateMutation,
  usePresentationTemplateCatalogQuery,
} from "../hooks/admin-queries"

export function PresentationTemplatePanel() {
  const { t } = useTranslation()
  const catalogQuery = usePresentationTemplateCatalogQuery()
  const analyze = useAnalyzePresentationTemplateMutation()

  const handleAnalyze = () => {
    analyze.mutate(undefined, {
      onSuccess: (data) => {
        toast.success(
          t("adminDashboard.systemConfig.presentationTemplate.analyzeSuccess", {
            defaultValue: "Analyzed {{count}} layouts",
            count: data.layout_count,
          })
        )
      },
      onError: (error: unknown) => {
        const message = error instanceof Error ? error.message : String(error)
        toast.error(
          t("adminDashboard.systemConfig.presentationTemplate.analyzeError", {
            defaultValue: "Analysis failed: {{message}}",
            message,
          })
        )
      },
    })
  }

  if (catalogQuery.isLoading) {
    return <Skeleton className="h-40 w-full" />
  }

  const noTemplate = catalogQuery.isError && isNoManagedTemplateError(catalogQuery.error)

  if (noTemplate) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.systemConfig.presentationTemplate.notConfigured", {
          defaultValue: "No managed PowerPoint template is configured for this tenant.",
        })}
      </p>
    )
  }

  const data = catalogQuery.data
  const layouts = data?.layouts ?? []
  const analyzed = data?.analyzed ?? false

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">
            {data?.template_filename ??
              t("adminDashboard.systemConfig.presentationTemplate.unknownFilename", {
                defaultValue: "(no template loaded)",
              })}
          </p>
          {analyzed ? (
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.systemConfig.presentationTemplate.layoutCount", {
                defaultValue: "{{count}} layouts analyzed",
                count: layouts.length,
              })}
            </p>
          ) : (
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.systemConfig.presentationTemplate.notAnalyzed", {
                defaultValue: "Template has not been analyzed yet.",
              })}
            </p>
          )}
        </div>
        <Button disabled={analyze.isPending} size="sm" variant="outline" onClick={handleAnalyze}>
          <RefreshCw className={`size-4 ${analyze.isPending ? "animate-spin" : ""}`} />
          {analyzed
            ? t("adminDashboard.systemConfig.presentationTemplate.reanalyze", {
                defaultValue: "Re-analyze",
              })
            : t("adminDashboard.systemConfig.presentationTemplate.analyze", {
                defaultValue: "Analyze",
              })}
        </Button>
      </div>

      {layouts.length > 0 && (
        <div className="border-border rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">#</th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("adminDashboard.systemConfig.presentationTemplate.previewHeader", {
                    defaultValue: "Preview",
                  })}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("adminDashboard.systemConfig.presentationTemplate.layoutNameHeader", {
                    defaultValue: "Layout",
                  })}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("adminDashboard.systemConfig.presentationTemplate.semanticRoleHeader", {
                    defaultValue: "Role",
                  })}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t("adminDashboard.systemConfig.presentationTemplate.placeholdersHeader", {
                    defaultValue: "Placeholders",
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {layouts.map((layout) => (
                <tr key={layout.layout_index} className="border-border border-t">
                  <td className="text-muted-foreground px-3 py-2 align-top">
                    {layout.layout_index}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {layout.preview_thumbnail_base64 ? (
                      <img
                        alt={layout.layout_name}
                        className="border-border h-16 w-28 rounded border object-contain"
                        src={`data:image/png;base64,${layout.preview_thumbnail_base64}`}
                      />
                    ) : (
                      <div className="border-border bg-muted/30 text-muted-foreground flex h-16 w-28 items-center justify-center rounded border text-[10px]">
                        {t("adminDashboard.systemConfig.presentationTemplate.noPreview", {
                          defaultValue: "no preview",
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">{layout.layout_name}</td>
                  <td className="px-3 py-2 align-top">
                    <Badge
                      variant={layout.analysis_status === "completed" ? "secondary" : "outline"}
                    >
                      {layout.semantic_role}
                    </Badge>
                  </td>
                  <td className="text-muted-foreground px-3 py-2 align-top">
                    {layout.placeholders.map((ph) => `${ph.idx}:${ph.type}`).join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
